import React, { useState, useRef, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGetShippingMetadata } from "api/tes/useGetShippingMetadata";
import AuthContext from "contexts/AuthContext";

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

export default function ShipmentItem({ shipment }) {
  const { tracking_number, created_at, takeback_id, id } = shipment;

  const { authTokens } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);

  // Query shipping metadata for label URL
  const { data: metadataItems } = useGetShippingMetadata(id);
  const labelMetadata = metadataItems.find(
    (metadata) => metadata.key === "label_url"
  );

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleViewLabel = () => {
    setMenuOpen(false);
    if (labelMetadata?.value) {
      try {
        const url = new URL(labelMetadata.value);
        const returnID = url.searchParams.get("returnID");

        let labelUrl = labelMetadata.value;
        if (returnID) {
          // Construct the PDF URL similar to other label viewing flows
          labelUrl = `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens?.access_token || ""}`;
        }

        // Open PDF in new tab
        window.open(labelUrl, "_blank");
      } catch (error) {
        console.error("Error constructing label URL:", error);
        // Fallback to original URL
        window.open(labelMetadata.value, "_blank");
      }
    }
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuButtonRef.current &&
        menuRef.current &&
        !menuButtonRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Map takeback statuses to shipping progress stages
  const getCurrentStage = (status) => {
    switch (status) {
      case "initiated":
        return 1; // Label created
      case "dropped-off":
        return 2; // Package collected
      case "received":
      case "processing":
      case "processed":
      case "complete":
        return 3; // Arrived at facility
      default:
        return 1;
    }
  };

  const latestStatus = metadataItems.find(
    (metadata) => metadata.key === "webhook_status_update"
  );

  const currentStage = latestStatus ? getCurrentStage(latestStatus.value) : 1;

  const stages = [
    {
      name: "Label created",
      date: formatDate(created_at),
      completed: currentStage >= 1,
    },
    {
      name: "Package collected",
      date:
        latestStatus?.status === "droppedOff"
          ? formatDate(latestStatus.processed_at || latestStatus.updated_at)
          : null,
      completed: currentStage >= 2,
    },
    {
      name: "Arrived at facility",
      date: ["received", "processing", "processed"].includes(
        latestStatus?.status
      )
        ? formatDate(latestStatus.processed_at || latestStatus.updated_at)
        : null,
      completed: currentStage >= 3,
    },
  ];

  return (
    <div className="bg-white shadow border border-gray-300 rounded-lg p-6">
      <div className="mb-4 flex justify-between items-center">
        <p className="text-lg font-medium text-gray-900">
          Tracking ID: {tracking_number || takeback_id}
        </p>
        <div className="relative">
          <button
            onClick={toggleMenu}
            className="material-symbols-outlined text-secondary hover:bg-gray-300 p-2 rounded-full"
            ref={menuButtonRef}
          >
            more_horiz
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20"
                ref={menuRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div
                  className="py-2"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <div className="px-4 py-1 flex flex-col gap-1">
                    <span className="block font-bold text-sm text-black">
                      Shipment Options
                    </span>
                    <div className="text-black-300 text-sm">
                      {tracking_number || takeback_id}
                    </div>
                  </div>
                  {labelMetadata?.value && (
                    <button
                      onClick={handleViewLabel}
                      className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                      role="menuitem"
                    >
                      View Shipping Label
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="relative">
        {/* Progress line */}
        <div
          className="absolute top-[0.8rem] left-6 w-full h-0.5 bg-gray-300"
          style={{ width: "calc(100% - 48px)" }}
        >
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{
              width: `${((stages.filter((s) => s.completed).length - 1) / (stages.length - 1)) * 100}%`,
            }}
          />
        </div>

        {/* Progress stages */}
        <div className="flex justify-between">
          {stages.map((stage, index) => (
            <div
              key={index}
              className={`flex flex-col ${index === 0 ? "items-start" : index === 2 ? "items-end" : "items-center"}`}
            >
              <div
                className={`mt-1 w-5 h-5 rounded-full border-2 border-white z-10 ${
                  stage.completed ? "bg-green" : "bg-gray-300"
                }`}
              ></div>
              <div className={`mt-1 text-sm`}>
                <div className={`${stage.completed ? "font-bold" : ""}`}>
                  {stage.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
